import React, { useEffect, useState } from "react";
import { FaInfo } from "react-icons/fa";
import styled from "styled-components";
import Conversation from "../components/Conversation";
import ServersList from "../components/ServersList";
import ProfileTab from "../components/ProfileTab";
import SectionSelectTab from "../components/SectionSelectTab";
import { breakpoints, colors, columnSize } from "../styles/theme";
import { useAuth } from "../providers/AuthProvider";
import RoomList from "../components/RoomList";
import SideTab from "../components/SideTab";
import { firestore } from "../lib/firebase";
import PageContainer from "../components/PageContainer";
import { useWebRTC } from "../providers/WebRTCProvider";
import {
  useCollectionData,
  useDocumentData,
} from "react-firebase-hooks/firestore";
import { useParams } from "react-router";
import ListWrapper from "../components/ListWrapper";
import Tab from "../components/Tab";
import SwipeBlock from "../components/SwipeBlock";

const Container = styled(PageContainer)`
  display: grid;
  grid-template-columns: ${columnSize} 1fr ${columnSize};

  > div {
    padding: 2rem;
  }

  @media (max-width: ${breakpoints.tablet}) {
    grid-template-columns: 1fr ${columnSize};
  }

  @media (max-width: ${breakpoints.phone}) {
    grid-template-columns: 1fr;
  }
`;

interface ServerProps {
  name: string;
  owner: string;
  photoURL: string;
  category: string;
  isPrivate: boolean;
  members: Array<any>;
}

interface RoomProps {
  id: string;
  name: string;
  type: string;
}

export default function Servers() {
  const { id } = useParams<any>();
  const [roomId, setRoomId] = useState("");
  const { user } = useAuth();
  const { servers } = user.database;
  const serverQuery = firestore.collection("servers").doc(id);
  const roomsQuery = serverQuery.collection("rooms");
  const [server, serverLoading] = useDocumentData<ServerProps>(serverQuery);
  const [rooms, roomsLoading] = useCollectionData<RoomProps>(roomsQuery, {
    idField: "id",
  });
  const {
    localStream,
    remoteStream,
    callId,
    initStreams,
    makeOffer,
    makeAnswer,
  } = useWebRTC();

  useEffect(() => {
    if (serverLoading || roomsLoading) return;
    if (!server || !rooms) return;
    if (!rooms.length) return setRoomId("");

    setRoomId(rooms[0].id);
  }, [server, serverLoading, rooms, roomsLoading]);

  function handleChangeRoom(id: string) {
    setRoomId(id);
  }

  async function handleJoinVoiceRoom(id: string) {
    if (!callId) return;

    await initStreams();

    const callDocExists = (await firestore.collection("calls").doc(id).get())
      .exists;
    !callDocExists ? await makeOffer(id) : await makeAnswer(id);
  }

  return (
    <Container>
      <SwipeBlock
        direction="right"
        breakpoint={breakpoints.tablet}
        style={{
          background: colors.background50,
          borderRight: `1px solid ${colors.background200}`,
        }}
      >
        <ListWrapper withSpacers>
          <ProfileTab />
          <SectionSelectTab />
          <ServersList values={servers} activeId={id} />
        </ListWrapper>
      </SwipeBlock>
      <ListWrapper withSpacers>
        {server && (
          <>
            <Tab
              avatarProps={{ imageSrc: server.photoURL, size: "md" }}
              name={server.name}
              icons={[{ iconComponent: FaInfo, onClick: () => {} }]}
            />
            <Conversation accessId={roomId} type="textRoom" />
          </>
        )}
      </ListWrapper>
      <SwipeBlock
        direction="left"
        breakpoint={breakpoints.phone}
        style={{
          background: colors.background50,
          borderLeft: `1px solid ${colors.background200}`,
        }}
      >
        <RoomList
          serverId={id}
          roomId={roomId}
          isOwner={user.auth.uid === server?.owner}
          values={rooms ? rooms : []}
          onTextRoomClick={handleChangeRoom}
          onVoiceRoomClick={handleJoinVoiceRoom}
        />
      </SwipeBlock>
    </Container>
  );
}
